import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import authNavigate from "../../../globalComponents/AuthNav";

/* ═══════════════════════════════════════════════════════════
   HITS SUMMARY TAB — backed by POST /hits-summary
   ═══════════════════════════════════════════════════════════ */

const DB_LABELS = { PUBMED: "PubMed", EMBASE: "Embase", COCHRANE: "Cochrane", ADHOC: "Adhoc" };

const HitsSummaryTab = ({ discoveryId, apiBaseUrl, headers }) => {
    const navigate = useNavigate();
    const [summaryData, setSummaryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 9;

    // Export state
    const [isExportingExcel, setIsExportingExcel] = useState(false);
    const [exportError, setExportError] = useState("");

    /* ── Fetch paginated hits summary ── */
    const fetchHitsSummary = useCallback(async (page = 1) => {
        if (!discoveryId) return;
        setLoading(true);
        setError("");
        try {
            const res = await axios.post(
                `${apiBaseUrl}/hits-summary`,
                { discovery_id: discoveryId, page, size: pageSize },
                { headers }
            );
            if (res.data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            if (res.data?.status === "success" && res.data.data) {
                setSummaryData(res.data.data);
            } else {
                setError(res.data?.message || "Failed to load hits summary");
            }
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            setError(err.response?.data?.message || err.message || "Error loading hits summary");
        } finally {
            setLoading(false);
        }
    }, [discoveryId, apiBaseUrl, headers, navigate]);

    useEffect(() => { fetchHitsSummary(currentPage); }, [currentPage, fetchHitsSummary]);

    /* ── Export strategies to Excel ── */
    const handleExportExcel = async () => {
        if (isExportingExcel) return;
        setIsExportingExcel(true);
        setExportError("");
        try {
            const res = await axios.post(
                `${apiBaseUrl}/export-strategies`,
                { discovery_id: discoveryId },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            if (res.data?.status === "success") {
                const file = res.data.data?.file;
                if (file?.content_base64) {
                    const bytes = atob(file.content_base64);
                    const arr = new Uint8Array(bytes.length);
                    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
                    const blob = new Blob([arr], { type: file.content_type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = file.filename || `search_strategies_${discoveryId}.xlsx`;
                    link.click();
                    URL.revokeObjectURL(url);
                }
            } else {
                setExportError(res.data?.message || "Failed to export strategies");
            }
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            setExportError(err.response?.data?.message || err.message || "Export failed");
        } finally {
            setIsExportingExcel(false);
        }
    };

    if (loading && !summaryData) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center vh-56 py-5">
                <i className="bi bi-hourglass-split ev-fs-30 ev-reg-p-blue d-block mb-2"></i>
                <p className="ev-small-p-dark">Loading hits summary...</p>
            </div>
        );
    }

    if (error && !summaryData) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center vh-56 py-5">
                <i className="bi bi-bar-chart-steps ev-fs-30 ev-reg-p-blue d-block mb-2"></i>
                <p className="ev-reg-p-dark ev-bold-med mb-1">Hits Summary</p>
                <p className="ev-small-p-dark">{error}</p>
                <button type="button" className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg mt-2" onClick={() => fetchHitsSummary(1)}>
                    <i className="bi bi-arrow-repeat me-1"></i> Retry
                </button>
            </div>
        );
    }

    if (!summaryData) return null;

    const d = summaryData;
    const totalPages = d.total_pages || 1;
    const startStep = (d.page - 1) * d.size + 1;
    const endStep = Math.min(startStep + (d.steps?.length || 0) - 1, d.total_steps);

    const getSubtotalByKey = (keyPattern) => {
        const match = (d.concept_subtotals || []).find((c) => {
            const k = (c.origin_key || "").toUpperCase();
            return k === keyPattern || k.includes(keyPattern);
        });
        return match?.hit_count;
    };

    const populationHits = getSubtotalByKey("POPULATION");
    const interventionHits = (() => {
        const intv = (d.concept_subtotals || []).find((c) => {
            const k = (c.origin_key || "").toUpperCase();
            return k.includes("INTERVENTION") || k.includes("COMPARATOR") || k.includes("COMPARISON");
        });
        return intv?.hit_count;
    })();
    const outcomesHits = (() => {
        const outcomes = (d.outcome_subtotals || []);
        if (outcomes.length === 0) return null;
        return outcomes.reduce((sum, o) => sum + (o.hit_count || 0), 0);
    })();

    return (
        <>
            {/* ── Summary cards row ── */}
            <div className="row g-2 mb-3">
                <div className="col">
                    <div className="ev-card-2 ev-padding-small ev-radius-xsmall">
                        <p className="ev-xsmall-p-dark ev-bold-reg mb-1">TOTAL STEPS</p>
                        <p className="ev-med-p-dark-blue ev-bold-med mb-0">
                            {d.total_steps} <span className="ev-xsmall-p-dark ev-bold-reg">success</span>
                        </p>
                    </div>
                </div>
                <div className="col">
                    <div className="ev-card-2 ev-padding-small ev-radius-xsmall">
                        <p className="ev-xsmall-p-dark ev-bold-reg mb-1">FINAL RESULTS</p>
                        <p className="ev-med-p-dark-blue ev-bold-med mb-0">
                            {d.final_result_count != null ? d.final_result_count.toLocaleString() : "—"}{" "}
                            <span className="ev-xsmall-p-dark ev-bold-reg">records</span>
                        </p>
                    </div>
                </div>
                {populationHits != null && (
                    <div className="col">
                        <div className="ev-card-2 ev-padding-small ev-radius-xsmall">
                            <p className="ev-xsmall-p-dark ev-bold-reg mb-1">POPULATION SUB-TOTAL</p>
                            <p className="ev-med-p-dark-blue ev-bold-med mb-0">{populationHits.toLocaleString()}</p>
                        </div>
                    </div>
                )}
                {interventionHits != null && (
                    <div className="col">
                        <div className="ev-card-2 ev-padding-small ev-radius-xsmall">
                            <p className="ev-xsmall-p-dark ev-bold-reg mb-1">INTERVENTION SUB-TOTAL</p>
                            <p className="ev-med-p-dark-blue ev-bold-med mb-0">{interventionHits.toLocaleString()}</p>
                        </div>
                    </div>
                )}
                {outcomesHits != null && (
                    <div className="col">
                        <div className="ev-card-2 ev-padding-small ev-radius-xsmall">
                            <p className="ev-xsmall-p-dark ev-bold-reg mb-1">OUTCOMES SUB-TOTAL</p>
                            <p className="ev-med-p-dark-blue ev-bold-med mb-0">{outcomesHits.toLocaleString()}</p>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Status line + action buttons ── */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <p className="ev-small-p-dark mb-0">
                    {d.success_count} of {d.total_steps} searches completed · Step-by-step breakdown of your PubMed query
                </p>
                <div className="d-flex gap-2 align-items-center">
                    {exportError && (
                        <span className="ev-xsmall-p-dark ev-alert-card-warning ev-radius-xsmall px-2 py-1">
                            <i className="bi bi-exclamation-triangle me-1"></i>{exportError}
                        </span>
                    )}
                    <button type="button" className="ev-btn-blue-fill-round ev-btn-small ev-bold-med"
                        onClick={handleExportExcel}
                        disabled={isExportingExcel || !d.total_steps}>
                        {isExportingExcel
                            ? <><span className="spinner-border spinner-border-sm me-1"></span> Exporting...</>
                            : <><i className="bi bi-download me-1"></i> Export strategies to Excel</>}
                    </button>
                </div>
            </div>

            {/* ── Steps table ── */}
            {/* Scrolls inside the list (header stays visible) instead of the whole page. */}
            <div
                className="ev-card-2 ev-radius-xsmall ev-padding-xsmall w-100 overflow-auto scrollable-element"
                style={{ maxHeight: "52vh" }}
            >
                {/* background: inherit down the chain so the stuck header uses the card colour */}
                <table className="table table-custom1 mb-0" style={{ background: "inherit" }}>
                    <thead style={{ position: "sticky", top: 0, zIndex: 2, background: "inherit" }}>
                        <tr style={{ background: "inherit" }}>
                            <th style={{ background: "inherit" }} className="ev-xsmall-p-dark ev-bold-med col-1">STEP</th>
                            <th style={{ background: "inherit" }} className="ev-xsmall-p-dark ev-bold-med col-3">DESCRIPTION</th>
                            <th style={{ background: "inherit" }} className="ev-xsmall-p-dark ev-bold-med text-end col-1">HITS</th>
                            <th style={{ background: "inherit" }} className="ev-xsmall-p-dark ev-bold-med col-1">STATUS</th>
                            <th style={{ background: "inherit" }} className="ev-xsmall-p-dark ev-bold-med col-5">SEARCH STRING</th>
                            <th style={{ background: "inherit" }} className="col-1"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {(d.steps || []).map((step) => (
                            <tr key={step.step_number}>
                                <td className="ev-small-p-dark ev-bold-med">#{step.step_number}</td>
                                <td className="ev-small-p-dark">{step.description || "—"}</td>
                                <td className="ev-small-p-dark ev-bold-med text-end">
                                    {step.hit_count != null ? step.hit_count.toLocaleString() : "—"}
                                </td>
                                <td>
                                    {step.status === "SUCCESS" ? (
                                        <span className="discovery-badge-green">
                                            <i className="bi bi-check-circle-fill me-1"></i> Success
                                        </span>
                                    ) : (
                                        <span className="discovery-badge-red">
                                            <i className="bi bi-x-circle-fill me-1"></i> Failed
                                        </span>
                                    )}
                                </td>
                                <td>
                                    <div className= "ev-xsmall-p-dark vh-10 scrollable-element">
                                        {step.query_string || "—"}
                                    </div>
                                </td>
                                <td className="text-center">
                                    <i className="bi bi-three-dots ev-small-p-dark" role="button"></i>
                                </td>
                            </tr>
                        ))}
                        {(!d.steps || d.steps.length === 0) && (
                            <tr>
                                <td colSpan={6} className="text-center ev-small-p-dark py-4">
                                    No search steps found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── Pagination ── */}
            {d.total_steps > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-2">
                    <p className="ev-xsmall-p-dark mb-0">
                        Showing steps {startStep}–{endStep} of {d.total_steps}
                    </p>
                    <div className="d-flex align-items-center gap-1">
                        <button type="button" className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall"
                            disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>
                            <i className="bi bi-chevron-left"></i>
                        </button>
                        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) pageNum = i + 1;
                            else if (currentPage <= 3) pageNum = i + 1;
                            else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                            else pageNum = currentPage - 2 + i;
                            return (
                                <button key={pageNum} type="button"
                                    className={`ev-btn-xsmall ev-radius-xsmall ev-bold-med ${currentPage === pageNum ? "ev-btn-blue-fill-round" : "ev-btn-light-fill"}`}
                                    onClick={() => setCurrentPage(pageNum)}>
                                    {pageNum}
                                </button>
                            );
                        })}
                        <button type="button" className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall"
                            disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
                            <i className="bi bi-chevron-right"></i>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default HitsSummaryTab;